'use client'

import { useState, useMemo, useEffect } from 'react'
import Link from 'next/link'
import {
  calculateTransitChart,
  lonToDegMin,
  SIGN_NAMES,
  SIGN_SYMBOLS,
  type PlanetPosition,
  type TransitChart,
} from '@/lib/astrology/calculate'

// ─── Display Data ───

const ELEMENT_COLORS: Record<string, string> = {
  fire: '#e88b8b',
  earth: '#a0a04e',
  air: '#6dd4e0',
  water: '#7baed4',
}

const SIGN_ELEMENTS = [
  'fire', 'earth', 'air', 'water', 'fire', 'earth',
  'air', 'water', 'fire', 'earth', 'air', 'water',
]

const PLANET_DISPLAY: Record<string, { symbol: string; color: string }> = {
  Sun:     { symbol: '☉', color: '#daa520' },
  Moon:    { symbol: '☽', color: '#e88bbd' },
  Mercury: { symbol: '☿', color: '#6bc5a0' },
  Venus:   { symbol: '♀', color: '#e88bbd' },
  Mars:    { symbol: '♂', color: '#e88b8b' },
  Jupiter: { symbol: '♃', color: '#d4a373' },
  Saturn:  { symbol: '♄', color: '#a0a04e' },
  Uranus:  { symbol: '♅', color: '#6dd4e0' },
  Neptune: { symbol: '♆', color: '#9b7bdf' },
  Pluto:   { symbol: '♇', color: '#b07bb0' },
}

const LOCATIONS = [
  { name: 'TP. Hồ Chí Minh', lat: 10.8231, lon: 106.6297 },
  { name: 'Hà Nội', lat: 21.0285, lon: 105.8542 },
  { name: 'San Diego', lat: 32.7157, lon: -117.1611 },
  { name: 'New York', lat: 40.7128, lon: -74.0060 },
  { name: 'London', lat: 51.5074, lon: -0.1278 },
  { name: 'Tokyo', lat: 35.6762, lon: 139.6503 },
  { name: 'Sydney', lat: -33.8688, lon: 151.2093 },
]

// ─── SVG Geometry ───

const CX = 300
const CY = 300
const R_OUTER = 280        // outermost circle (tick marks)
const R_ZODIAC_OUT = 272   // outer edge of zodiac sign ring
const R_ZODIAC_IN = 234    // inner edge of zodiac ring
const R_PLANET = 202       // planet glyph radius
const R_HOUSE_NUM = 155    // house number radius
const R_INNER = 78         // center circle
const RING_COLOR = '#6b6b52' // golden/olive ring stroke

function lonToAngle(lon: number, asc: number): number {
  return ((180 + lon - asc) % 360 + 360) % 360
}

function polar(angleDeg: number, radius: number): { x: number; y: number } {
  const rad = angleDeg * Math.PI / 180
  return { x: CX + radius * Math.cos(rad), y: CY + radius * Math.sin(rad) }
}

function houseMidLon(cusp1: number, cusp2: number): number {
  const diff = ((cusp1 - cusp2) % 360 + 360) % 360
  return ((cusp1 - diff / 2) % 360 + 360) % 360
}

// Spread overlapping planet glyphs radially
function layoutPlanets(planets: PlanetPosition[], asc: number) {
  const items = planets
    .map(p => ({ ...p, angle: lonToAngle(p.longitude, asc), r: R_PLANET }))
    .sort((a, b) => a.angle - b.angle)

  const MIN_GAP = 9
  for (let i = 1; i < items.length; i++) {
    let gap = items[i].angle - items[i - 1].angle
    if (gap < 0) gap += 360
    if (gap < MIN_GAP) {
      // Offset radially to avoid overlap
      const idx = i % 3
      items[i].r = R_PLANET + (idx === 1 ? -18 : idx === 2 ? 18 : 0)
    }
  }
  return items
}

// ─── Chart SVG ───

function TransitChartSVG({ chart }: { chart: TransitChart }) {
  const { ascendant, midheaven, planets, houses } = chart
  const laid = layoutPlanets(planets, ascendant)

  return (
    <svg viewBox="0 0 600 600" className="w-full" xmlns="http://www.w3.org/2000/svg">
      {/* Dark background */}
      <rect x="0" y="0" width="600" height="600" rx="16" fill="#1a1a1a" />

      {/* Zodiac ring fill — darker band between zodiac outer and inner */}
      {SIGN_SYMBOLS.map((_, i) => {
        const startLon = i * 30
        const endLon = (i + 1) * 30
        const a1 = lonToAngle(startLon, ascendant)
        const a2 = lonToAngle(endLon, ascendant)
        // Wedge fill for each sign
        const element = SIGN_ELEMENTS[i]
        const color = ELEMENT_COLORS[element]
        const outerStart = polar(a1, R_ZODIAC_OUT)
        const outerEnd = polar(a2, R_ZODIAC_OUT)
        const innerStart = polar(a1, R_ZODIAC_IN)
        const innerEnd = polar(a2, R_ZODIAC_IN)
        const diff = ((a2 - a1) % 360 + 360) % 360
        const large = diff > 180 ? 1 : 0
        const d = [
          `M ${outerStart.x} ${outerStart.y}`,
          `A ${R_ZODIAC_OUT} ${R_ZODIAC_OUT} 0 ${large} 1 ${outerEnd.x} ${outerEnd.y}`,
          `L ${innerEnd.x} ${innerEnd.y}`,
          `A ${R_ZODIAC_IN} ${R_ZODIAC_IN} 0 ${large} 0 ${innerStart.x} ${innerStart.y}`,
          'Z',
        ].join(' ')
        return <path key={`zfill-${i}`} d={d} fill={color} fillOpacity="0.06" />
      })}

      {/* Main structural circles */}
      <circle cx={CX} cy={CY} r={R_OUTER} fill="none" stroke={RING_COLOR} strokeWidth="1.5" strokeOpacity="0.7" />
      <circle cx={CX} cy={CY} r={R_ZODIAC_OUT} fill="none" stroke={RING_COLOR} strokeWidth="1" strokeOpacity="0.5" />
      <circle cx={CX} cy={CY} r={R_ZODIAC_IN} fill="none" stroke={RING_COLOR} strokeWidth="1.5" strokeOpacity="0.7" />
      <circle cx={CX} cy={CY} r={R_INNER} fill="none" stroke={RING_COLOR} strokeWidth="1" strokeOpacity="0.4" />

      {/* Degree tick marks on outer edge */}
      {Array.from({ length: 360 }, (_, i) => {
        const a = lonToAngle(i, ascendant)
        const isBoundary = i % 30 === 0
        const len = i % 10 === 0 ? 6 : i % 5 === 0 ? 4 : 2
        if (isBoundary) return null // sign boundaries drawn separately
        const p1 = polar(a, R_OUTER)
        const p2 = polar(a, R_OUTER - len)
        return (
          <line
            key={`tick-${i}`}
            x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
            stroke={RING_COLOR}
            strokeOpacity={i % 10 === 0 ? 0.6 : 0.25}
            strokeWidth="0.5"
          />
        )
      })}

      {/* Sign divider lines (through zodiac ring and ticks) */}
      {Array.from({ length: 12 }, (_, i) => {
        const a = lonToAngle(i * 30, ascendant)
        const p1 = polar(a, R_ZODIAC_IN)
        const p2 = polar(a, R_OUTER)
        return (
          <line
            key={`sign-div-${i}`}
            x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
            stroke={RING_COLOR} strokeOpacity="0.7" strokeWidth="1"
          />
        )
      })}

      {/* Sign glyphs in zodiac ring */}
      {SIGN_SYMBOLS.map((sym, i) => {
        const midLon = i * 30 + 15
        const a = lonToAngle(midLon, ascendant)
        const p = polar(a, (R_ZODIAC_OUT + R_ZODIAC_IN) / 2)
        return (
          <text
            key={`sign-${i}`}
            x={p.x} y={p.y}
            textAnchor="middle" dominantBaseline="central"
            fontSize="16" fontWeight="500"
            fill={ELEMENT_COLORS[SIGN_ELEMENTS[i]]}
            opacity="0.9"
          >
            {sym}
          </text>
        )
      })}

      {/* House cusp lines */}
      {houses.map((h) => {
        const a = lonToAngle(h.longitude, ascendant)
        const isAngle = h.house === 1 || h.house === 4 || h.house === 7 || h.house === 10
        const p1 = polar(a, R_INNER)
        const p2 = polar(a, R_ZODIAC_IN)
        return (
          <line
            key={`cusp-${h.house}`}
            x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
            stroke={RING_COLOR}
            strokeOpacity={isAngle ? 0.7 : 0.25}
            strokeWidth={isAngle ? 1.5 : 0.75}
          />
        )
      })}

      {/* House numbers */}
      {houses.map((h, i) => {
        const nextCusp = houses[(i + 1) % 12].longitude
        const mid = houseMidLon(h.longitude, nextCusp)
        const a = lonToAngle(mid, ascendant)
        const p = polar(a, R_HOUSE_NUM)
        return (
          <text
            key={`hnum-${h.house}`}
            x={p.x} y={p.y}
            textAnchor="middle" dominantBaseline="central"
            fontSize="13" fill="#8a8a78" opacity="0.6"
            fontWeight="400"
          >
            {h.house}
          </text>
        )
      })}

      {/* Cusp degree labels — all 12 house cusps on outer ring */}
      {houses.map((h) => {
        const dm = lonToDegMin(h.longitude)
        const a = lonToAngle(h.longitude, ascendant)
        const color = ELEMENT_COLORS[SIGN_ELEMENTS[dm.sign]]
        // Place labels just outside the outer ring, rotated to follow the angle
        const p = polar(a, R_OUTER + 16)
        const pMin = polar(a, R_OUTER + 28)
        return (
          <g key={`cdeg-${h.house}`}>
            <text
              x={p.x} y={p.y - 3}
              textAnchor="middle" dominantBaseline="central"
              fontSize="8" fill={color} opacity="0.8"
            >
              {String(dm.deg).padStart(2, '0')}°
            </text>
            <text
              x={pMin.x} y={pMin.y - 3}
              textAnchor="middle" dominantBaseline="central"
              fontSize="7" fill={color} opacity="0.55"
            >
              {String(dm.min).padStart(2, '0')}&apos;
            </text>
            <text
              x={p.x} y={p.y + 8}
              textAnchor="middle" dominantBaseline="central"
              fontSize="10" fill={color} opacity="0.9" fontWeight="500"
            >
              {SIGN_SYMBOLS[dm.sign]}
            </text>
          </g>
        )
      })}

      {/* ASC arrow (left-pointing triangle at the ASC position) */}
      {(() => {
        const a = 180 // ASC is always at 180° in chart space
        const tip = polar(a, R_ZODIAC_IN - 2)
        const back = polar(a, R_ZODIAC_IN + 12)
        const wing1 = polar(a - 2.5, R_ZODIAC_IN + 10)
        const wing2 = polar(a + 2.5, R_ZODIAC_IN + 10)
        // Also draw ASC label
        const labelP = polar(a, R_ZODIAC_IN - 14)
        return (
          <g>
            <polygon
              points={`${tip.x},${tip.y} ${wing1.x},${wing1.y} ${back.x},${back.y} ${wing2.x},${wing2.y}`}
              fill={ELEMENT_COLORS[SIGN_ELEMENTS[Math.floor(ascendant / 30)]]}
              opacity="0.8"
            />
          </g>
        )
      })()}

      {/* Planet glyphs */}
      {laid.map((p) => {
        const display = PLANET_DISPLAY[p.name]
        const pos = polar(p.angle, p.r)
        return (
          <text
            key={p.name}
            x={pos.x} y={pos.y}
            textAnchor="middle" dominantBaseline="central"
            fontSize="17" fontWeight="500"
            fill={display.color}
          >
            {display.symbol}
          </text>
        )
      })}

      {/* Planet degree + sign labels (below/above each glyph) */}
      {laid.map((p) => {
        const display = PLANET_DISPLAY[p.name]
        // Place degree label toward center from glyph
        const labelR = p.r - 14
        const pos = polar(p.angle, labelR)
        return (
          <g key={`pdeg-${p.name}`}>
            <text
              x={pos.x} y={pos.y - 1}
              textAnchor="middle" dominantBaseline="central"
              fontSize="6.5" fill={display.color} opacity="0.7"
              fontFamily="monospace"
            >
              {String(p.degree).padStart(2, '0')}°
            </text>
            <text
              x={pos.x} y={pos.y + 7}
              textAnchor="middle" dominantBaseline="central"
              fontSize="7.5" fill={ELEMENT_COLORS[SIGN_ELEMENTS[p.signIndex]]} opacity="0.5"
            >
              {SIGN_SYMBOLS[p.signIndex]}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

// ─── Page ───

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', weekday: 'short' })
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })
}

function toInputDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function toInputTime(d: Date): string {
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  return `${h}:${m}`
}

export default function AstrologyChartPage() {
  const [mounted, setMounted] = useState(false)
  const [dateStr, setDateStr] = useState('')
  const [timeStr, setTimeStr] = useState('')
  const [locationIdx, setLocationIdx] = useState(0)
  const [isLive, setIsLive] = useState(true)
  const [liveDate, setLiveDate] = useState<Date | null>(null)

  // Initialize on mount to avoid hydration mismatch
  useEffect(() => {
    const now = new Date()
    setDateStr(toInputDate(now))
    setTimeStr(toInputTime(now))
    setLiveDate(now)
    setMounted(true)
  }, [])

  // Live mode: update every 30 seconds
  useEffect(() => {
    if (!isLive) return
    const iv = setInterval(() => setLiveDate(new Date()), 30000)
    return () => clearInterval(iv)
  }, [isLive])

  const date = useMemo(() => {
    if (!mounted || !liveDate) return null
    if (isLive) return liveDate
    const [y, m, d] = dateStr.split('-').map(Number)
    const [hh, mm] = timeStr.split(':').map(Number)
    return new Date(y, m - 1, d, hh, mm)
  }, [mounted, isLive, liveDate, dateStr, timeStr])

  const loc = LOCATIONS[locationIdx]

  const chart = useMemo(() => {
    if (!date) return null
    try {
      return calculateTransitChart(date, loc.lat, loc.lon)
    } catch {
      return null
    }
  }, [date, loc.lat, loc.lon])

  if (!mounted || !chart) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-8">
        <nav className="text-xs text-muted-foreground mb-4">
          <Link href="/astrology" className="hover:underline">Astrology</Link>
          <span className="mx-1">/</span>
          <span>Transit Chart</span>
        </nav>
        <h1 className="text-2xl font-bold mb-2">Transits</h1>
        <p className="text-sm text-muted-foreground">Đang tải...</p>
      </div>
    )
  }

  // date is guaranteed non-null here (we return early if !chart, which requires date)
  const chartDate = date!
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const offset = -chartDate.getTimezoneOffset()
  const tzLabel = `UTC${offset >= 0 ? '+' : ''}${offset / 60}`

  return (
    <div className="container mx-auto max-w-3xl px-4 py-8">
      {/* Header */}
      <nav className="text-xs text-muted-foreground mb-4">
        <Link href="/astrology" className="hover:underline">Astrology</Link>
        <span className="mx-1">/</span>
        <span>Transit Chart</span>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-bold">Transits</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Bản đồ hành tinh thời gian thực — Tropical / Equal House
        </p>
      </div>

      {/* Controls */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6 space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setIsLive(true); setLiveDate(new Date()) }}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              isLive
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border hover:bg-muted/50'
            }`}
          >
            Live
          </button>
          <button
            onClick={() => { setIsLive(false); setDateStr(toInputDate(new Date())); setTimeStr(toInputTime(new Date())) }}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              !isLive
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border hover:bg-muted/50'
            }`}
          >
            Custom
          </button>
        </div>

        {!isLive && (
          <div className="flex flex-wrap gap-3">
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Date</label>
              <input
                type="date"
                value={dateStr}
                onChange={e => setDateStr(e.target.value)}
                className="text-xs bg-background border border-border rounded px-2 py-1.5"
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Time</label>
              <input
                type="time"
                value={timeStr}
                onChange={e => setTimeStr(e.target.value)}
                className="text-xs bg-background border border-border rounded px-2 py-1.5"
              />
            </div>
          </div>
        )}

        <div>
          <label className="text-[10px] text-muted-foreground block mb-1">Location</label>
          <select
            value={locationIdx}
            onChange={e => setLocationIdx(Number(e.target.value))}
            className="text-xs bg-background border border-border rounded px-2 py-1.5"
          >
            {LOCATIONS.map((l, i) => (
              <option key={i} value={i}>{l.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Chart container — dark background to match the astronomical aesthetic */}
      <div className="relative mb-6 bg-[#1a1a1a] rounded-2xl p-4 sm:p-6 overflow-hidden">
        {/* Chart info (top-left overlay) */}
        <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-10 text-[10px] leading-relaxed space-y-0.5 pointer-events-none">
          <div className="font-semibold text-sm text-white/90">Transits</div>
          <div className="text-white/50 text-[10px]">Event</div>
          <div className="text-white/50">{formatDate(chartDate)}</div>
          <div className="text-white/50">{formatTime(chartDate)} {tzLabel}</div>
          <div className="text-white/50">{loc.name}</div>
          <div className="text-[#e88b8b] italic text-[10px]">Tropical</div>
          <div className="text-[#e88b8b] italic text-[10px]">Equal House</div>
        </div>

        <TransitChartSVG chart={chart} />
      </div>

      {/* Planet positions table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold">Vị Trí Hành Tinh</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="text-left px-4 py-2 font-medium">Planet</th>
                <th className="text-left px-4 py-2 font-medium">Sign</th>
                <th className="text-right px-4 py-2 font-medium">Degree</th>
                <th className="text-right px-4 py-2 font-medium">Longitude</th>
              </tr>
            </thead>
            <tbody>
              {chart.planets.map(p => {
                const display = PLANET_DISPLAY[p.name]
                return (
                  <tr key={p.name} className="border-b border-border/50 last:border-0">
                    <td className="px-4 py-2">
                      <span style={{ color: display.color }} className="mr-1.5">{display.symbol}</span>
                      {p.name}
                    </td>
                    <td className="px-4 py-2">
                      <span style={{ color: ELEMENT_COLORS[SIGN_ELEMENTS[p.signIndex]] }} className="mr-1">
                        {SIGN_SYMBOLS[p.signIndex]}
                      </span>
                      {SIGN_NAMES[p.signIndex]}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {String(p.degree).padStart(2, '0')}° {String(p.minute).padStart(2, '0')}&apos;
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-muted-foreground">
                      {p.longitude.toFixed(4)}°
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* House cusps table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden mt-4">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold">House Cusps</h3>
          <p className="text-[10px] text-muted-foreground">
            ASC {(() => { const d = lonToDegMin(chart.ascendant); return `${String(d.deg).padStart(2,'0')}° ${SIGN_SYMBOLS[d.sign]} ${String(d.min).padStart(2,'0')}'` })()}
            {' | '}
            MC {(() => { const d = lonToDegMin(chart.midheaven); return `${String(d.deg).padStart(2,'0')}° ${SIGN_SYMBOLS[d.sign]} ${String(d.min).padStart(2,'0')}'` })()}
          </p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-px bg-border/30">
          {chart.houses.map(h => {
            const dm = lonToDegMin(h.longitude)
            const isAngle = h.house === 1 || h.house === 4 || h.house === 7 || h.house === 10
            return (
              <div
                key={h.house}
                className={`bg-card px-3 py-2 text-xs ${isAngle ? 'font-semibold' : ''}`}
              >
                <span className="text-muted-foreground mr-1">{h.house}.</span>
                <span style={{ color: ELEMENT_COLORS[SIGN_ELEMENTS[dm.sign]] }}>
                  {SIGN_SYMBOLS[dm.sign]}
                </span>
                {' '}
                <span className="font-mono">
                  {String(dm.deg).padStart(2, '0')}°{String(dm.min).padStart(2, '0')}&apos;
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
